<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
          </svg>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ t('kanban.anexos.title') }}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.anexos.subtitle') }}</p>
          <p class="text-xs text-blue-500 dark:text-blue-400 mt-1">{{ t('kanban.anexos.pasteHint') }}</p>
        </div>
      </div>
      
      <!-- Botão de Upload -->
      <button
        @click="openFileDialog"
        :class="[
          'flex items-center justify-center rounded-lg transition-colors duration-200 bg-blue-500 hover:bg-blue-600 text-white',
          iconOnlyUpload ? 'w-10 h-10 p-0' : 'space-x-2 px-4 py-2'
        ]"
        :title="iconOnlyUpload ? t('kanban.anexos.addFileTitle') : undefined"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
        <span v-if="!iconOnlyUpload">{{ t('kanban.anexos.addFile') }}</span>
      </button>
    </div>

    <!-- Input de Upload (oculto) -->
    <input
      ref="fileInput"
      type="file"
      multiple
      accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
      @change="handleFileSelect"
      class="hidden"
    />

    <!-- Loading -->
    <div v-if="uploading" class="mb-6">
      <div class="flex items-center space-x-3 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
        <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
        <span class="text-blue-700 dark:text-blue-300">{{ t('kanban.anexos.uploading') }}</span>
      </div>
    </div>

    <!-- Drop Zone Visual -->
    <div 
      class="mb-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center bg-gray-50 dark:bg-gray-700/50 transition-colors duration-200"
      :class="{ 'border-blue-400 bg-blue-50 dark:bg-blue-900/30': isDragOver }"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleDrop"
    >
      <div class="flex flex-col items-center space-y-3">
        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
        <div>
          <p class="text-sm text-gray-600 dark:text-gray-300 mb-1">{{ t('kanban.anexos.dropHint') }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('kanban.anexos.formats') }}</p>
          <p class="text-xs text-blue-500 dark:text-blue-400 mt-1">{{ t('kanban.anexos.pasteHintShort') }}</p>
        </div>
      </div>
    </div>

    <!-- Tabs de Categorias -->
    <div class="mb-6">
      <div class="border-b border-gray-200 dark:border-gray-700">
        <nav class="-mb-px flex space-x-8">
          <button
            @click="activeTab = 'imagens'"
            :class="activeTab === 'imagens' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'"
            class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
          >
            <div class="flex items-center space-x-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <span>{{ t('kanban.anexos.tabImages', { count: imagens.length }) }}</span>
            </div>
          </button>
          <button
            @click="activeTab = 'arquivos'"
            :class="activeTab === 'arquivos' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'"
            class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
          >
            <div class="flex items-center space-x-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <span>{{ t('kanban.anexos.tabFiles', { count: arquivos.length }) }}</span>
            </div>
          </button>
        </nav>
      </div>
    </div>

    <!-- Conteúdo das Tabs -->
    <div class="min-h-[200px]">
      <!-- Tab Imagens -->
      <div v-if="activeTab === 'imagens'" class="space-y-4">
        <div v-if="imagens.length === 0" class="text-center py-12">
          <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
          </div>
          <p class="text-gray-500 dark:text-gray-400">{{ t('kanban.anexos.noImages') }}</p>
          <p class="text-sm text-gray-400 dark:text-gray-500">{{ t('kanban.anexos.noImagesHint') }}</p>
        </div>
        
        <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div
            v-for="imagem in imagens"
            :key="imagem.id"
            class="group relative bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden cursor-pointer"
            @click="openImagePreview(imagem)"
          >
            <img
              :src="resolveAnexoUrl(imagem)"
              :alt="imagem.nomeOriginal"
              class="w-full h-24 object-cover group-hover:scale-105 transition-transform duration-200"
              @error="handleImageError"
            />
            <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
              <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                </svg>
              </div>
            </div>
            
            <!-- Botão de deletar -->
            <button
              @click.stop="deleteAnexo(imagem.id)"
              class="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Tab Arquivos -->
      <div v-if="activeTab === 'arquivos'" class="space-y-4">
        <div v-if="arquivos.length === 0" class="text-center py-12">
          <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
          <p class="text-gray-500 dark:text-gray-400">{{ t('kanban.anexos.noFiles') }}</p>
          <p class="text-sm text-gray-400 dark:text-gray-500">{{ t('kanban.anexos.noFilesHint') }}</p>
        </div>
        
        <div v-else class="space-y-3">
          <div
            v-for="arquivo in arquivos"
            :key="arquivo.id"
            class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
          >
            <div class="flex items-center space-x-4 flex-1">
              <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <span class="text-2xl">{{ getFileIcon(arquivo.tipoArquivo) }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {{ arquivo.nomeOriginal }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ formatFileSize(arquivo.tamanho) }} • {{ arquivo.dataUpload }}
                </p>
              </div>
            </div>
            
            <div class="flex items-center space-x-2">
              <button
                @click="downloadFile(arquivo)"
                class="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition-colors duration-200"
                :title="t('kanban.anexos.download')"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </button>
              <button
                @click="deleteAnexo(arquivo.id)"
                class="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition-colors duration-200"
                :title="t('kanban.anexos.delete')"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Preview de Imagem -->
    <div
      v-if="showImagePreview"
      class="fixed inset-0 z-[13050] flex items-center justify-center bg-black bg-opacity-75"
      data-image-preview-overlay
      role="dialog"
      aria-modal="true"
      :aria-label="t('kanban.chat.previewAria')"
      @click="closeImagePreview"
    >
      <div class="relative max-w-4xl max-h-[90vh] p-4" @click.stop>
        <img
          :src="selectedImage ? resolveAnexoUrl(selectedImage) : ''"
          :alt="selectedImage?.nomeOriginal"
          class="max-w-full max-h-full object-contain rounded-lg"
        />
        <button
          type="button"
          data-image-preview-close
          :aria-label="t('common.close')"
          @click.stop="closeImagePreview"
          class="absolute top-4 right-4 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all duration-200"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import Swal from 'sweetalert2'
import { pushEscapeLayer } from '@/utils/escapeLayerStack'

const { t } = useI18n()

const props = defineProps({
  taskId: {
    type: Number,
    required: false
  },
  leadId: {
    type: Number,
    required: false
  },
  clienteId: {
    type: Number,
    required: false
  },
  reloadSignal: {
    type: Number,
    required: false,
    default: 0
  },
  /** No modal do lead: botão de upload só com ícone */
  iconOnlyUpload: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['anexosUpdated'])

// Refs
const fileInput = ref(null)
const uploading = ref(false)
const activeTab = ref('imagens')
const anexos = ref([])
const showImagePreview = ref(false)
const selectedImage = ref(null)
const isDragOver = ref(false)

// Computed
const imagens = computed(() => anexos.value.filter(anexo => anexo.ehImagem))
const arquivos = computed(() => anexos.value.filter(anexo => !anexo.ehImagem))

const hasAnexoContext = () => !!(props.taskId || props.leadId || props.clienteId)

// Methods
const handlePaste = async (event) => {
  const target = event?.target
  if (target?.closest?.('[data-chat-input="true"]')) {
    return
  }

  const items = event.clipboardData?.items
  if (!items) return
  
  const imageFiles = []
  
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    
    // Verificar se é uma imagem
    if (item.type.startsWith('image/')) {
      const originalFile = item.getAsFile()
      if (originalFile) {
        // Criar um novo arquivo com nome personalizado
        const timestamp = new Date().getTime()
        const extension = originalFile.type.split('/')[1] || 'png'
        const fileName = `pasted-image-${timestamp}.${extension}`
        
        // Criar um novo File object com o nome correto
        const newFile = new File([originalFile], fileName, {
          type: originalFile.type,
          lastModified: originalFile.lastModified
        })
        
        imageFiles.push(newFile)
      }
    }
  }
  
  if (imageFiles.length > 0) {
    event.preventDefault() // Prevenir o comportamento padrão
    
    uploading.value = true
    
    try {
      const uploadPromises = imageFiles.map(file => uploadFile(file))
      await Promise.all(uploadPromises)
      
      await loadAnexos()
      emit('anexosUpdated')
      
    } catch (error) {
      console.error('❌ Erro ao fazer upload das imagens coladas:', error)
      alert(t('kanban.anexos.uploadPasteError'))
    } finally {
      uploading.value = false
    }
  }
}

const handleDrop = async (event) => {
  isDragOver.value = false
  
  const files = Array.from(event.dataTransfer.files)
  if (files.length === 0) return

  uploading.value = true
  
  try {
    const uploadPromises = files.map(file => uploadFile(file))
    await Promise.all(uploadPromises)
    
    await loadAnexos()
    emit('anexosUpdated')
    
  } catch (error) {
    console.error('❌ Erro ao fazer upload dos arquivos soltos:', error)
    alert(t('kanban.anexos.uploadError'))
  } finally {
    uploading.value = false
  }
}

const openFileDialog = () => {
  fileInput.value?.click()
}

const handleFileSelect = async (event) => {
  const files = Array.from(event.target.files)
  if (files.length === 0) return

  uploading.value = true
  
  try {
    const uploadPromises = files.map(file => uploadFile(file))
    await Promise.all(uploadPromises)
    
    await loadAnexos()
    emit('anexosUpdated')
    
    // Limpar input
    event.target.value = ''
    
  } catch (error) {
    console.error('❌ Erro ao fazer upload dos arquivos:', error)
    alert(t('kanban.anexos.uploadError'))
  } finally {
    uploading.value = false
  }
}

const uploadFile = async (file) => {
  try {
    // Validar tamanho (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error(`Arquivo ${file.name} é muito grande. Máximo 10MB.`)
    }

    // Converter para base64
    const base64 = await fileToBase64(file)
    
    let request = {}
    let endpoint = ''
    
    if (props.taskId) {
      request = {
        taskId: props.taskId,
        nomeArquivo: file.name,
        nomeOriginal: file.name,
        tipoArquivo: file.type || 'application/octet-stream',
        tamanho: file.size,
        conteudoBase64: base64
      }
      endpoint = '/anexos/upload'
    } else if (props.leadId) {
      request = {
        leadId: props.leadId,
        nomeArquivo: file.name,
        nomeOriginal: file.name,
        tipoArquivo: file.type || 'application/octet-stream',
        tamanho: file.size,
        conteudoBase64: base64
      }
      endpoint = '/anexos-lead/upload'
    } else if (props.clienteId) {
      request = {
        clienteId: props.clienteId,
        nomeArquivo: file.name,
        nomeOriginal: file.name,
        tipoArquivo: file.type || 'application/octet-stream',
        tamanho: file.size,
        conteudoBase64: base64
      }
      endpoint = '/anexos-cliente/upload'
    }

    const token = localStorage.getItem('token')
    const response = await api.post(endpoint, request, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    console.log('✅ Arquivo enviado com sucesso:', file.name)
    return response.data

  } catch (error) {
    console.error('❌ Erro ao enviar arquivo:', error)
    throw error
  }
}

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const base64 = reader.result.split(',')[1] // Remove data:type;base64,
      resolve(base64)
    }
    reader.onerror = error => reject(error)
  })
}

const loadAnexos = async () => {
  try {
    const token = localStorage.getItem('token')
    let endpoint = ''
    
    if (props.taskId) {
      endpoint = `/anexos/task/${props.taskId}`
    } else if (props.leadId) {
      endpoint = `/anexos-lead/lead/${props.leadId}`
    } else if (props.clienteId) {
      endpoint = `/anexos-cliente/cliente/${props.clienteId}`
    } else {
      console.error('❌ TaskId, LeadId ou ClienteId é obrigatório')
      return
    }
    
    const response = await api.get(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    
    anexos.value = response.data || []
    
  } catch (error) {
    console.error('❌ Erro ao carregar anexos:', error)
    anexos.value = []
  }
}

const deleteAnexo = async (anexoId) => {
  const result = await Swal.fire({
    title: t('kanban.anexos.swal.deleteTitle'),
    text: t('kanban.anexos.swal.deleteText'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('kanban.anexos.swal.confirmDelete'),
    cancelButtonText: t('kanban.anexos.swal.cancel'),
    reverseButtons: true
  })

  if (!result.isConfirmed) {
    return
  }

  try {
    const token = localStorage.getItem('token')
    
    // Usar endpoint correto baseado no tipo (task ou lead)
    let endpoint = ''
    if (props.taskId) {
      endpoint = `/anexos/${anexoId}`
    } else if (props.leadId) {
      endpoint = `/anexos-lead/${anexoId}`
    } else if (props.clienteId) {
      endpoint = `/anexos-cliente/${anexoId}`
    }
    
    await api.delete(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    await loadAnexos()
    emit('anexosUpdated')
    
    // Mostrar sucesso
    Swal.fire({
      title: t('kanban.anexos.swal.deletedTitle'),
      text: t('kanban.anexos.swal.deletedText'),
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    })
    
  } catch (error) {
    console.error('❌ Erro ao deletar anexo:', error)
    Swal.fire({
      title: t('kanban.anexos.swal.errorTitle'),
      text: t('kanban.anexos.swal.errorText'),
      icon: 'error',
      confirmButtonText: t('kanban.anexos.swal.ok')
    })
  }
}

const openImagePreview = (imagem) => {
  selectedImage.value = imagem
  showImagePreview.value = true
}

const closeImagePreview = () => {
  showImagePreview.value = false
  selectedImage.value = null
}

let unregisterAnexoPreviewEscape = null

watch(showImagePreview, (open) => {
  if (unregisterAnexoPreviewEscape) {
    unregisterAnexoPreviewEscape()
    unregisterAnexoPreviewEscape = null
  }
  if (open) {
    unregisterAnexoPreviewEscape = pushEscapeLayer(closeImagePreview)
  }
})

const resolveAnexoUrl = (anexo) => {
  if (!anexo) return ''
  if (props.taskId && anexo.id) {
    return `${api.defaults.baseURL}/anexos/${anexo.id}/conteudo`
  }
  return anexo.urlAcesso || ''
}

const downloadFile = (arquivo) => {
  const link = document.createElement('a')
  link.href = resolveAnexoUrl(arquivo)
  link.download = arquivo.nomeOriginal
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const getFileIcon = (tipoArquivo) => {
  const ext = tipoArquivo?.split('/')[1]?.toLowerCase() || ''
  
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return '🖼️'
  } else if (ext === 'pdf') {
    return '📄'
  } else if (['doc', 'docx'].includes(ext)) {
    return '📝'
  } else if (['xls', 'xlsx'].includes(ext)) {
    return '📊'
  } else if (['ppt', 'pptx'].includes(ext)) {
    return '📽️'
  } else if (ext === 'txt') {
    return '📃'
  } else if (['zip', 'rar'].includes(ext)) {
    return '🗜️'
  } else {
    return '📎'
  }
}

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
}

const handleImageError = (event) => {
  event.target.style.display = 'none'
}

// Watch
watch(() => [props.taskId, props.leadId, props.clienteId], () => {
  if (hasAnexoContext()) {
    loadAnexos()
  }
}, { immediate: true })

watch(() => props.reloadSignal, async () => {
  if (hasAnexoContext()) {
    await loadAnexos()
  }
})

// Lifecycle
onMounted(() => {
  if (hasAnexoContext()) {
    loadAnexos()
  }
  
  // Adicionar event listener para Ctrl+V
  document.addEventListener('paste', handlePaste)
})

// Cleanup
onUnmounted(() => {
  if (unregisterAnexoPreviewEscape) {
    unregisterAnexoPreviewEscape()
    unregisterAnexoPreviewEscape = null
  }
  document.removeEventListener('paste', handlePaste)
})
</script>
